import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { api, formatUSD } from "../../lib/api";
import { toast } from "sonner";
import { Briefcase, CurrencyDollar, CheckCircle, Eye, Receipt, Lightning } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";

import { AgentManagementTable } from "./agents/AgentManagementTable";
import { CreateAgentDialog } from "./agents/CreateAgentDialog";
import { AgentDetailDialog } from "./agents/AgentDetailDialog";
import BulkTopUpDialog from "./agents/BulkTopUpDialog";

/**
 * AgentsAdmin — admin-only management surface for agents + commissions.
 *
 * This page is composed of three sub-modules (under `./agents/`):
 *   • AgentManagementTable — searchable / expandable card-issuance dashboard
 *   • CreateAgentDialog    — modal to create a new agent
 *   • AgentDetailDialog    — deep-dive on one agent (commissions, mark-paid, suspend)
 *
 * The legacy Commission table (bank + week/month/year/unpaid totals) stays
 * inline below because it's tightly coupled to the page-level summary state.
 */
export default function AgentsAdmin() {
  const [summary, setSummary] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // agent_id of currently-open detail
  const [selectedForTopup, setSelectedForTopup] = useState(() => new Set()); // agent_ids
  const [topupOpen, setTopupOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/admin/agent-commissions/summary");
      setSummary(r.data);
    } catch {
      toast.error("Failed to load agents");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // After Stripe Checkout success-redirect (?bulk_topup=1&session_id=…),
  // poll the bulk-topup status endpoint so the in-memory float balances
  // reflect the new credits even if the Stripe webhook hasn't fired yet.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const batchId = params.get("batch_id");
    const status = params.get("bulk_topup");
    if (status === "1" && sid) {
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await api.get(`/admin/agent-bulk-topups/${sid}/status`);
          if (r.data?.status === "paid") {
            const cnt = r.data?.batch?.items?.length || 0;
            const sum = r.data?.batch?.total_amount || 0;
            toast.success(`Bulk top-up complete · ${cnt} agent${cnt === 1 ? "" : "s"} credited · ${formatUSD(sum)}`);
            refresh();
            navigate("/admin/agents", { replace: true });
            return;
          }
        } catch {
          /* keep retrying */
        }
        if (attempts < 6) setTimeout(poll, 1500);
        else {
          toast.info("Bulk top-up payment processing… It will be credited once Stripe confirms.");
          navigate("/admin/agents", { replace: true });
        }
      };
      poll();
    } else if (status === "0" && batchId) {
      toast.info("Bulk top-up cancelled — no agents were charged or credited.");
      navigate("/admin/agents", { replace: true });
    }
  }, [location.search, navigate, refresh]);

  const toggleAgent = (agentId) => {
    setSelectedForTopup(prev => {
      const n = new Set(prev);
      if (n.has(agentId)) n.delete(agentId);
      else n.add(agentId);
      return n;
    });
  };

  const selectedAgents = useMemo(
    () => summary.rows
      .filter(r => selectedForTopup.has(r.agent_id) && r.status !== "suspended")
      .map(r => ({
        agent_id: r.agent_id,
        agent_code: r.agent_code,
        full_name: r.full_name,
        email: r.email,
      })),
    [summary.rows, selectedForTopup],
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#4A5C50]">Loading…</div>;

  const totals = summary.totals || {};
  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Admin</p>
            <h1 className="serif text-5xl mt-2">Agents & commissions</h1>
            <p className="text-[#4A5C50] mt-2 max-w-2xl">
              Manage agent accounts, view per-agent earnings, and mark commission batches as paid.
              Each agent has a unique 6-character code traceable to every transaction they initiated.
            </p>
          </div>
          <CreateAgentDialog onCreated={refresh} />
        </div>

        <div className="grid md:grid-cols-4 gap-4 mt-10">
          <StatTile icon={<Briefcase size={20} weight="duotone" />} label="Active agents" value={totals.agent_count ?? 0} testId="totals-count" />
          <StatTile icon={<CurrencyDollar size={20} weight="duotone" />} label="Lifetime earned" value={formatUSD(totals.lifetime ?? 0)} testId="totals-lifetime" />
          <StatTile icon={<Receipt size={20} weight="duotone" />} label="Unpaid" value={formatUSD(totals.unpaid ?? 0)} testId="totals-unpaid" tone="amber" />
          <StatTile icon={<CheckCircle size={20} weight="duotone" />} label="Paid" value={formatUSD(totals.paid ?? 0)} testId="totals-paid" tone="emerald" />
        </div>

        <AgentManagementTable rows={summary.rows} onSelectAgent={setSelected} />

        {selectedForTopup.size > 0 && (
          <div
            className="sticky top-[80px] z-30 mt-6 bg-[#184A30] text-white rounded-2xl px-5 py-3 flex items-center justify-between gap-3 shadow-lg shadow-[#184A30]/20"
            data-testid="agents-bulk-topup-bar"
          >
            <div className="text-sm">
              <span className="font-semibold" data-testid="agents-bulk-topup-count">{selectedForTopup.size}</span> agent
              {selectedForTopup.size === 1 ? "" : "s"} selected
              <button
                onClick={() => setSelectedForTopup(new Set())}
                className="ml-3 text-xs text-white/70 hover:text-white underline underline-offset-2"
                data-testid="agents-bulk-topup-clear"
              >
                Clear
              </button>
            </div>
            <Button
              size="sm"
              onClick={() => setTopupOpen(true)}
              className="bg-[#CBA35C] hover:bg-[#b08e4a] text-white border-0"
              data-testid="agents-bulk-topup-open-btn"
            >
              <Lightning size={14} className="mr-1" weight="fill" /> Bulk top-up · {selectedForTopup.size} agent{selectedForTopup.size === 1 ? "" : "s"}
            </Button>
          </div>
        )}

        <h2 className="serif text-3xl mt-12">Commission table</h2>
        <p className="text-[#4A5C50] text-xs mt-1">All agents · sortable by code, weekly, monthly, yearly totals. Tick the box to seed an agent&apos;s operating-float balance via bulk Stripe Checkout.</p>
        <div className="bg-white border border-[#E5E0D8] rounded-2xl mt-4 overflow-x-auto" data-testid="agents-commission-table">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="text-left text-[#4A5C50] text-[11px] uppercase tracking-widest">
              <tr>
                <th className="p-4 w-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                    checked={summary.rows.length > 0 && summary.rows.filter(r => r.status !== "suspended").every(r => selectedForTopup.has(r.agent_id))}
                    ref={(el) => {
                      if (el) {
                        const active = summary.rows.filter(r => r.status !== "suspended");
                        const some = active.some(r => selectedForTopup.has(r.agent_id));
                        const all = active.length > 0 && active.every(r => selectedForTopup.has(r.agent_id));
                        el.indeterminate = some && !all;
                      }
                    }}
                    onChange={() => {
                      const active = summary.rows.filter(r => r.status !== "suspended").map(r => r.agent_id);
                      if (active.every(id => selectedForTopup.has(id))) {
                        setSelectedForTopup(new Set());
                      } else {
                        setSelectedForTopup(new Set(active));
                      }
                    }}
                    data-testid="agents-bulk-topup-select-all"
                    disabled={summary.rows.filter(r => r.status !== "suspended").length === 0}
                  />
                </th>
                <th className="p-4">Code</th>
                <th className="p-4">Agent</th>
                <th className="p-4">Phone</th>
                <th className="p-4">Address</th>
                <th className="p-4">Bank · Account</th>
                <th className="p-4 text-right">Float</th>
                <th className="p-4 text-right">Week</th>
                <th className="p-4 text-right">Month</th>
                <th className="p-4 text-right">Year</th>
                <th className="p-4 text-right">Unpaid</th>
                <th className="p-4 text-right">Lifetime</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.length === 0 ? (
                <tr><td colSpan={14} className="p-10 text-center text-[#4A5C50]">No agents yet. Tap &quot;Create agent&quot; to onboard your first.</td></tr>
              ) : summary.rows.map(r => (
                <tr key={r.agent_id} className={`border-t border-[#E5E0D8] ${selectedForTopup.has(r.agent_id) ? "bg-[#CBA35C]/10" : ""}`} data-testid={`agent-row-${r.agent_code}`}>
                  <td className="p-4">
                    {r.status === "suspended" ? (
                      <span className="block w-4 h-4 opacity-30 rounded border border-[#E5E0D8]" />
                    ) : (
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                        checked={selectedForTopup.has(r.agent_id)}
                        onChange={() => toggleAgent(r.agent_id)}
                        data-testid={`agent-topup-checkbox-${r.agent_code}`}
                      />
                    )}
                  </td>
                  <td className="p-4 mono font-semibold text-[#112318]">{r.agent_code}</td>
                  <td className="p-4">
                    <div className="font-semibold text-[#112318]">{r.full_name}</div>
                    <button
                      type="button"
                      onClick={() => setSelected(r.agent_id)}
                      className="text-xs text-[#184A30] hover:text-[#0d3320] underline decoration-dotted underline-offset-4 mono text-left"
                      data-testid={`agent-email-link-${r.agent_code}`}
                      title="View commission detail"
                    >
                      {r.email}
                    </button>
                  </td>
                  <td className="p-4 mono text-xs">{r.phone || "—"}</td>
                  <td className="p-4 text-xs text-[#4A5C50] max-w-[200px] truncate">{r.address || "—"}</td>
                  <td className="p-4 text-xs">
                    <div className="font-semibold">{r.bank_name || "—"}</div>
                    <div className="mono text-[#4A5C50]">
                      {r.bank_account_number ? `•••• ${r.bank_account_number.slice(-4)}` : "—"}
                    </div>
                  </td>
                  <td className="p-4 text-right mono font-semibold text-[#CBA35C]" data-testid={`agent-float-${r.agent_code}`}>
                    {formatUSD(r.float_balance_usd || 0)}
                  </td>
                  <td className="p-4 text-right mono">{formatUSD(r.week_total)}</td>
                  <td className="p-4 text-right mono">{formatUSD(r.month_total)}</td>
                  <td className="p-4 text-right mono">{formatUSD(r.year_total)}</td>
                  <td className="p-4 text-right mono font-semibold text-amber-700">{formatUSD(r.unpaid_total)}</td>
                  <td className="p-4 text-right mono font-semibold">{formatUSD(r.lifetime_total)}</td>
                  <td className="p-4">
                    {r.status === "suspended" ? (
                      <span className="inline-block rounded-full bg-rose-100 text-rose-700 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5">Suspended</span>
                    ) : (
                      <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5">Active</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelected(r.agent_id)} data-testid={`agent-view-${r.agent_code}`}>
                      <Eye size={14} className="mr-1" /> Detail
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <AgentDetailDialog
          agentId={selected}
          onClose={() => setSelected(null)}
          onChanged={refresh}
        />
      )}

      <BulkTopUpDialog
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        selectedAgents={selectedAgents}
      />
    </div>
  );
}

function StatTile({ icon, label, value, sub, testId, tone }) {
  const toneCls = tone === "amber" ? "text-amber-700" : tone === "emerald" ? "text-emerald-700" : "text-[#184A30]";
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid={testId}>
      <div className={toneCls}>{icon}</div>
      <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-4">{label}</p>
      <p className={`serif text-3xl mt-1 ${toneCls}`}>{value}</p>
      {sub && <p className="text-xs text-[#4A5C50] mt-1">{sub}</p>}
    </div>
  );
}
